import { MAX_CONTAINER_WIDTH } from '@/components/layout/Container';
import { COLORS } from '@/styles/color';
import {
  BREAKPOINT_PADDING_VAR,
  mediaBreakpointDown,
  mediaBreakpointOnly,
  mediaBreakpointUp,
  square,
} from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize } from '@/styles/typography/utils';
import styled, { css, keyframes } from 'styled-components';

const Wrapper = styled.div`
  position: relative;
  padding: 120px 0;

  ${mediaBreakpointUp('tablet')} {
    padding: 140px 0;
    display: grid;
    column-gap: 20px;
    grid-template-columns: repeat(12, 1fr);
  }

  ${mediaBreakpointUp('desktop')} {
    padding: 78px 0 58px;
    grid-template-columns: repeat(18, 1fr);
  }

  ${mediaBreakpointUp('desktopWide')} {
    padding: 184px 0;
    grid-template-columns: repeat(16, 1fr);
  }
`;

const ContentWrapper = styled.div`
  position: relative;

  ${mediaBreakpointUp('tablet')} {
    grid-column: 2 / -2;
  }

  ${mediaBreakpointUp('desktop')} {
    grid-column: 5 / -5;
    padding: 0 15px;
  }
`;

const Heading = styled.h2`
  ${customTextSize(24, 30)}
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  text-align: center;
  letter-spacing: -0.01em;
  margin-bottom: 20px;

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(32, 38)}
  }
`;

const Text = styled.div`
  ${customTextSize(14, 20)}
  text-align: center;

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(16, 24)}
  }
`;

const BubbleGroupCSS = css`
  position: absolute;
  width: calc(100% + (${BREAKPOINT_PADDING_VAR} * 2) - 20px);
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  ${mediaBreakpointOnly('mobile')} {
    left: calc(${BREAKPOINT_PADDING_VAR} * -1 + 10px);
  }

  ${mediaBreakpointOnly('tablet')} {
    left: 0;
  }

  ${mediaBreakpointUp('tablet')} {
    width: 100%;
  }

  ${mediaBreakpointUp('desktop')} {
    display: block;
    top: -78px;
    bottom: -58px;
    width: calc((100vw - 100% - 40px) / 2);
    max-width: calc((${MAX_CONTAINER_WIDTH}px + ${BREAKPOINT_PADDING_VAR} - 100%) / 2);
  }

  ${mediaBreakpointUp('desktopWide')} {
    width: calc((100vw - 100%) / 2);
    top: -184px;
    bottom: -184px;
  }
`;

const TopBubbleGroup = styled.div`
  ${BubbleGroupCSS}

  ${mediaBreakpointDown('tablet')} {
    bottom: calc(100% + 20px);
    align-items: flex-start;

    > :nth-child(odd) {
      margin-top: 58px;
    }

    > :nth-child(3) {
      margin-top: 38px;
    }
  }

  ${mediaBreakpointUp('desktop')} {
    right: 100%;

    > :nth-child(1) {
      bottom: 11%;
      left: 0;
    }

    > :nth-child(2) {
      top: 8%;
      left: 12%;
    }

    > :nth-child(3) {
      top: 38%;
      left: 39%;
    }

    > :nth-child(4) {
      top: 3%;
      right: 5%;
    }

    > :nth-child(5) {
      bottom: 8%;
      right: 5%;
    }
  }
`;

const BottomBubbleGroup = styled.div`
  ${BubbleGroupCSS}

  ${mediaBreakpointDown('tablet')} {
    top: calc(100% + 20px);
    align-items: flex-end;

    > :nth-child(odd) {
      margin-bottom: 58px;
    }

    > :nth-child(3) {
      margin-bottom: 38px;
    }
  }

  ${mediaBreakpointUp('desktop')} {
    left: 100%;

    > :nth-child(1) {
      top: 5%;
      left: 0;
    }

    > :nth-child(2) {
      bottom: 10%;
      left: 10%;
    }

    > :nth-child(3) {
      top: 35%;
      left: 40%;
    }

    > :nth-child(4) {
      bottom: 14%;
      right: 8%;
    }

    > :nth-child(5) {
      top: 10%;
      right: 0;
    }
  }
`;

const BobAnimation = keyframes`
  0% {
    -webkit-transform: translateY(-3px);
    transform: translateY(-3px);
  }
  50% {
    -webkit-transform: translateY(3px);
    transform: translateY(3px);
  }
  100% {
    -webkit-transform: translateY(-3px);
    transform: translateY(-3px);
  }
`;

interface BubbleProps {
  $delay: number;
}

const Bubble = styled.div<BubbleProps>`
  ${square(38)}
  border-radius: 100%;
  box-shadow: 0px 0px 9px rgba(0, 27, 56, 0.1);
  overflow: hidden;

  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: ${COLORS.WHITE};
  animation-name: ${BobAnimation};
  animation-duration: 3s;
  animation-delay: ${(props) => props.$delay}s;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
  animation-fill-mode: forwards;
  animation-direction: alternate;

  > * {
    width: 100%;
    height: 100%;
  }

  ${mediaBreakpointUp('tablet')} {
    ${square(47)}
    box-shadow: 0px 0px 12px rgba(0, 27, 56, 0.1);
  }

  ${mediaBreakpointUp('desktop')} {
    ${square(60)}
    box-shadow: 0px 0px 15px rgba(0, 27, 56, 0.1);
    position: absolute;
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${square(100)}
    box-shadow: 0px 0px 24px rgba(0, 27, 56, 0.1);
  }
`;

const ButtonWrapper = styled.div`
  margin-top: 30px;
  text-align: center;
`;

const integrationsCloudComponents = {
  Wrapper,
  ContentWrapper,
  Heading,
  Text,
  TopBubbleGroup,
  BottomBubbleGroup,
  Bubble,
  ButtonWrapper,
};

export default integrationsCloudComponents;
